import type { CustomerGroupPreview, CustomerGroupSelection, QbCustomer } from "@/api/accounting"

/**
 * Splitting one Ahold check across the brand's accounting customers — the apply page's side of
 * `server/src/accounting/customer_split.rs`. Everything here is display math in cents; the server
 * recomputes the real plan from the invoices and credit memo lines before anything posts.
 */

/** One accounting customer's side of the check once groups that share a customer are merged. */
export interface CustomerSide {
  customer_id: string
  customer_name: string
  keys: string[]
  net_cents: number
}

/**
 * A deficit moved through the inter-customer clearing account: a dummy invoice on
 * `deficit_customer_id` (netted negative), an equal dummy credit memo on `donor_customer_id`.
 */
export interface ClearingTransfer {
  deficit_customer_id: string
  donor_customer_id: string
  amount_cents: number
}

export function to_cents(amount: number | string): number {
  return Math.round(Number(amount) * 100)
}

export function customer_group_label(key: string): string {
  return key === "" ? "No legacy vendor number" : `Legacy vendor ${key}`
}

/**
 * Pre-fill each group with the customer saved on an earlier apply, else the one its own invoices are
 * billed to — whichever still exists. A first split check used to open with every picker empty, so
 * no invoices loaded and each line claimed its (synced, open) invoice did not exist.
 */
export function seed_group_customers(groups: CustomerGroupPreview[], customers: QbCustomer[]): Record<string, string> {
  const known = new Set(customers.map((c: QbCustomer) => c.remote_id))
  const seeded: Record<string, string> = {}
  for (const group of groups) {
    const choice = [group.saved_customer_remote_id, group.suggested_customer_remote_id].find(
      (id: string | null | undefined): id is string => !!id && known.has(id)
    )
    if (choice) {
      seeded[group.key] = choice
    }
  }
  return seeded
}

/** What an unassigned invoice line says while the vendor accounts still lack a customer. */
export function choose_group_customers_hint(system_label: string): string {
  return `Choose a ${system_label} customer for each legacy vendor account below — its invoices load once it has one.`
}

/** The distinct customers chosen so far, sorted — the scope for the invoice picker. */
export function chosen_customer_ids(groups: CustomerGroupPreview[], selections: Record<string, string>): string[] {
  const ids = groups.map((g: CustomerGroupPreview) => selections[g.key]).filter((id: string | undefined): id is string => !!id)
  return Array.from(new Set(ids)).sort()
}

export function all_groups_assigned(groups: CustomerGroupPreview[], selections: Record<string, string>): boolean {
  return groups.every((g: CustomerGroupPreview) => !!selections[g.key])
}

/** Merge the groups by their chosen customer. Groups without a customer yet are left out. */
export function merge_customer_sides(
  groups: CustomerGroupPreview[],
  selections: Record<string, string>,
  customers: QbCustomer[]
): CustomerSide[] {
  const sides = new Map<string, CustomerSide>()
  for (const group of groups) {
    const customer_id = selections[group.key]
    if (!customer_id) {
      continue
    }
    const side = sides.get(customer_id) ?? {
      customer_id,
      customer_name: customers.find((c: QbCustomer) => c.remote_id === customer_id)?.display_name ?? customer_id,
      keys: [],
      net_cents: 0,
    }
    side.keys.push(group.key)
    side.net_cents += to_cents(group.net)
    sides.set(customer_id, side)
  }
  return Array.from(sides.values()).sort((a: CustomerSide, b: CustomerSide) => a.customer_id.localeCompare(b.customer_id))
}

/**
 * The transfers that bring the negative sides toward zero, drawing on the positive sides
 * largest-first — mirrors the server's `plan_clearing_transfers`. Each is a dummy invoice and a
 * dummy credit memo of the same amount, so the clearing account nets to zero. `shortfall_cents > 0`
 * means the check itself nets negative: that much stays as open credit on the negative customer's
 * credit memo.
 */
export function plan_clearing_transfers(sides: CustomerSide[]): { transfers: ClearingTransfer[]; shortfall_cents: number } {
  const donors = sides
    .filter((s: CustomerSide) => s.net_cents > 0)
    .map((s: CustomerSide) => ({ id: s.customer_id, available: s.net_cents }))
    .sort((a, b) => b.available - a.available || a.id.localeCompare(b.id))
  const deficits = sides
    .filter((s: CustomerSide) => s.net_cents < 0)
    .map((s: CustomerSide) => ({ id: s.customer_id, needed: -s.net_cents }))
    .sort((a, b) => a.id.localeCompare(b.id))

  const transfers: ClearingTransfer[] = []
  let shortfall_cents = 0
  for (const deficit of deficits) {
    for (const donor of donors) {
      if (deficit.needed === 0) {
        break
      }
      const amount = Math.min(deficit.needed, donor.available)
      if (amount === 0) {
        continue
      }
      donor.available -= amount
      deficit.needed -= amount
      transfers.push({ deficit_customer_id: deficit.id, donor_customer_id: donor.id, amount_cents: amount })
    }
    shortfall_cents += deficit.needed
  }
  return { transfers, shortfall_cents }
}

/** What each customer's side carries once the transfers are in (negative = credit left open). */
export function payment_cents_by_customer(sides: CustomerSide[], transfers: ClearingTransfer[]): Record<string, number> {
  const cash: Record<string, number> = {}
  for (const side of sides) {
    cash[side.customer_id] = side.net_cents
  }
  for (const t of transfers) {
    cash[t.deficit_customer_id] += t.amount_cents
    cash[t.donor_customer_id] -= t.amount_cents
  }
  return cash
}

/**
 * What to send on apply. Every group given the same customer is an ordinary single-customer apply
 * (`single_customer_id`); otherwise one selection per group.
 */
export function build_group_selections(
  groups: CustomerGroupPreview[],
  selections: Record<string, string>,
  customers: QbCustomer[]
): { single_customer_id: string | null; customer_groups: CustomerGroupSelection[] } {
  const ids = chosen_customer_ids(groups, selections)
  if (ids.length === 1 && all_groups_assigned(groups, selections)) {
    return { single_customer_id: ids[0], customer_groups: [] }
  }
  return {
    single_customer_id: null,
    customer_groups: groups.map((g: CustomerGroupPreview) => ({
      key: g.key,
      customer_id: selections[g.key] ?? "",
      customer_name: customers.find((c: QbCustomer) => c.remote_id === selections[g.key])?.display_name,
    })),
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const group = (key: string, net: number, saved?: string): CustomerGroupPreview => ({
    key,
    saved_customer_remote_id: saved,
    transaction_count: 1,
    invoice_cash: Math.max(net, 0),
    deduction_total: Math.min(net, 0),
    net,
  })
  const customers = [
    { remote_id: "4417", display_name: "Ahold USA" },
    { remote_id: "5520", display_name: "Delhaize America" },
  ] as QbCustomer[]

  describe("customer groups", () => {
    it("seeds saved customers only when they still exist", () => {
      const groups = [group("61524", 100, "4417"), group("W006477000", 100, "gone"), group("", -5)]
      expect(seed_group_customers(groups, customers)).toEqual({ "61524": "4417" })
    })

    it("falls back to the customer a group's invoices are billed to", () => {
      const suggested = (g: CustomerGroupPreview, id: string): CustomerGroupPreview => ({ ...g, suggested_customer_remote_id: id })
      const groups = [
        suggested(group("61524", 100), "4417"),
        suggested(group("W006477000", 100, "5520"), "4417"),
        suggested(group("99", 100), "gone"),
      ]
      expect(seed_group_customers(groups, customers)).toEqual({ "61524": "4417", W006477000: "5520" })
    })

    it("merges the unassigned rows into the customer they are given", () => {
      const groups = [group("61524", 2352.2), group("W006477000", 2981.12), group("", -25)]
      const sides = merge_customer_sides(groups, { "61524": "4417", W006477000: "5520", "": "4417" }, customers)
      expect(sides.map((s: CustomerSide) => [s.customer_name, s.net_cents, s.keys])).toEqual([
        ["Ahold USA", 232720, ["61524", ""]],
        ["Delhaize America", 298112, ["W006477000"]],
      ])
      expect(plan_clearing_transfers(sides)).toEqual({ transfers: [], shortfall_cents: 0 })
    })

    it("covers a negative side from the positive one", () => {
      const sides = merge_customer_sides([group("61524", -750), group("W006477000", 4800)], { "61524": "4417", W006477000: "5520" }, customers)
      const { transfers, shortfall_cents } = plan_clearing_transfers(sides)
      expect(transfers).toEqual([{ deficit_customer_id: "4417", donor_customer_id: "5520", amount_cents: 75000 }])
      expect(shortfall_cents).toBe(0)
      expect(payment_cents_by_customer(sides, transfers)).toEqual({ "4417": 0, "5520": 405000 })
    })

    it("books Ahold 2000268892 the way Super Coffee did by hand", () => {
      // Ahold +$39,063.76, Delhaize −$38,163.29: dummy invoice and dummy credit memo of $38,163.29, payment $900.47.
      const sides = merge_customer_sides([group("61524", 39063.76), group("W006477000", -38163.29)], { "61524": "4417", W006477000: "5520" }, customers)
      const { transfers, shortfall_cents } = plan_clearing_transfers(sides)
      expect(transfers).toEqual([{ deficit_customer_id: "5520", donor_customer_id: "4417", amount_cents: 3816329 }])
      expect(shortfall_cents).toBe(0)
      expect(payment_cents_by_customer(sides, transfers)).toEqual({ "4417": 90047, "5520": 0 })
    })

    it("leaves what a net-negative check cannot cover as open credit, outside clearing", () => {
      const sides = merge_customer_sides([group("61524", -500), group("W006477000", 300)], { "61524": "4417", W006477000: "5520" }, customers)
      const { transfers, shortfall_cents } = plan_clearing_transfers(sides)
      expect(transfers).toEqual([{ deficit_customer_id: "4417", donor_customer_id: "5520", amount_cents: 30000 }])
      expect(shortfall_cents).toBe(20000)
      expect(payment_cents_by_customer(sides, transfers)).toEqual({ "4417": -20000, "5520": 0 })
    })

    it("sends a single customer when every group gets the same one", () => {
      const groups = [group("61524", 100), group("W006477000", 100)]
      expect(build_group_selections(groups, { "61524": "4417", W006477000: "4417" }, customers)).toEqual({
        single_customer_id: "4417",
        customer_groups: [],
      })
      const split = build_group_selections(groups, { "61524": "4417", W006477000: "5520" }, customers)
      expect(split.single_customer_id).toBeNull()
      expect(split.customer_groups).toEqual([
        { key: "61524", customer_id: "4417", customer_name: "Ahold USA" },
        { key: "W006477000", customer_id: "5520", customer_name: "Delhaize America" },
      ])
    })

    it("is not fully assigned until every group has a customer", () => {
      const groups = [group("61524", 100), group("W006477000", 100)]
      expect(all_groups_assigned(groups, { "61524": "4417" })).toBe(false)
      expect(chosen_customer_ids(groups, { "61524": "4417" })).toEqual(["4417"])
    })
  })
}
